import { useSyncExternalStore } from "react";
import {
  AuthError,
  ensureAnonymousSession as ensureAnonymousSessionClient,
  isAuthAvailable,
  signInWithApple as signInWithAppleClient,
  signInWithGoogle as signInWithGoogleClient,
  signInWithServerToken,
  signInWithTestUser as signInWithTestUserClient,
  signOut as signOutClient,
  subscribeAuth,
  type AuthUser,
  type SignInOptions,
} from "./authClient";
import { getFirebaseApp } from "./firebase";
import { getFlag } from "./flags";
import { clearLocalAppData } from "./clearLocalData";
import { exitSharedRoom } from "./openRoom";
import type { SignInSurface } from "./events";
import { markSignInFailed, markSignInStarted } from "./signInFunnel";

/**
 * Thin React layer on top of the Firebase auth client. Components should
 * import from `./auth` (this file), never `./authClient` directly — that
 * keeps the React tree decoupled from Firebase types so unit tests can
 * mock the whole surface in one place if needed.
 */

type AuthStatus = "loading" | "signed-in" | "signed-out" | "unavailable";

interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
}

let state: AuthState = {
  status: isAuthAvailable() ? "loading" : "unavailable",
  user: null,
};

const listeners = new Set<() => void>();
let subscribed = false;
let unsubFromFirebase: (() => void) | null = null;

function emit() {
  for (const l of listeners) l();
}

function ensureSubscribed() {
  if (subscribed) return;
  subscribed = true;
  if (!isAuthAvailable()) {
    state = { status: "unavailable", user: null };
    return;
  }
  unsubFromFirebase = subscribeAuth((user) => {
    state = {
      status: user ? "signed-in" : "signed-out",
      user,
    };
    emit();
  });
}

function getSnapshot(): AuthState {
  return state;
}

function subscribe(listener: () => void): () => void {
  ensureSubscribed();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const SERVER_SNAPSHOT: AuthState = { status: "loading", user: null };
function getServerSnapshot(): AuthState {
  return SERVER_SNAPSHOT;
}

/** React hook — returns the current auth state. Re-renders on changes. */
export function useAuth(): AuthState {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/** Read the current user synchronously (outside React). */
export function getCurrentUser(): AuthUser | null {
  ensureSubscribed();
  return state.user;
}

/**
 * Google sign-in. `surface` names the call to action for the sign-in
 * funnel (`sign_in_started` / `sign_in_failed`, and the `surface` prop
 * on the `sign_up` / `login` that follows).
 */
export async function signInWithGoogle(
  surface: SignInSurface,
  opts?: SignInOptions
): Promise<AuthUser> {
  markSignInStarted(surface);
  try {
    return await signInWithGoogleClient(opts);
  } catch (err) {
    reportSignInFailure(surface, err);
    throw err;
  }
}

/**
 * Apple sign-in. Same funnel bookkeeping as Google. Shown only while the
 * `apple_sign_in` flag is on (the provider has to be configured in the
 * Firebase console first).
 */
export async function signInWithApple(
  surface: SignInSurface,
  opts?: SignInOptions
): Promise<AuthUser> {
  markSignInStarted(surface);
  try {
    return await signInWithAppleClient(opts);
  } catch (err) {
    reportSignInFailure(surface, err);
    throw err;
  }
}

/** True when a signed-in user has an account (not an anonymous session). */
export function hasAccount(user: AuthUser | null | undefined): user is AuthUser {
  return !!user && !user.isAnonymous;
}

/**
 * Anonymous first-run sessions: on while the `anonymous_trial` flag is on
 * and Firebase is configured. The metered callables give such a session
 * `BILLING_CONFIG.anonymousCredits` before asking for an account.
 */
export function anonymousTrialEnabled(): boolean {
  return isAuthAvailable() && getFlag("anonymous_trial");
}

/**
 * Make sure there is a uid to call the AI with: the signed-in user, or a
 * fresh anonymous session. Throws `AuthError` when neither is possible.
 */
export async function ensureAnonymousSession(): Promise<AuthUser> {
  return ensureAnonymousSessionClient();
}

/** Ask the server to email a six-digit code. Throws `AuthError` on failure. */
export async function requestEmailCode(email: string): Promise<void> {
  const fn = await otpCallable<{ email: string }, { ok: boolean }>("requestEmailCode");
  try {
    await fn({ email });
  } catch (err) {
    throw toOtpError(err);
  }
}

/**
 * Verify the emailed code and sign in. An anonymous session keeps its uid
 * (the account was created *around* it). Returns whether that happened.
 */
export async function verifyEmailCode(
  surface: SignInSurface,
  email: string,
  code: string
): Promise<{ user: AuthUser; linked: boolean; isNewUser: boolean }> {
  markSignInStarted(surface);
  try {
    const fn = await otpCallable<
      { email: string; code: string },
      { token: string; linked: boolean; isNewUser: boolean }
    >("verifyEmailCode");
    let res;
    try {
      res = await fn({ email, code });
    } catch (err) {
      throw toOtpError(err);
    }
    const user = await signInWithServerToken(res.data.token, res.data.linked);
    return { user, linked: res.data.linked, isNewUser: res.data.isNewUser };
  } catch (err) {
    reportSignInFailure(surface, err);
    throw err;
  }
}

async function otpCallable<TReq, TRes>(name: string) {
  const app = getFirebaseApp();
  if (!app) throw new AuthError("not-configured", "Sign-in isn't configured for this environment.");
  const { getFunctions, httpsCallable } = await import("firebase/functions");
  const region = (import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION as string | undefined) || "us-central1";
  return httpsCallable<TReq, TRes>(getFunctions(app, region), name);
}

function toOtpError(err: unknown): AuthError {
  const code = (err as { code?: string } | null)?.code ?? "";
  const message = (err as { message?: string } | null)?.message ?? "";
  if (/EMAIL_OTP_UNAVAILABLE/.test(message)) {
    return new AuthError("not-configured", "Email sign-in isn't available right now. Try Google instead.");
  }
  if (/OTP_RATE_LIMITED/.test(message) || /resource-exhausted/.test(code)) {
    return new AuthError("rate-limited", message.replace(/^.*?:\s*/, "") || "Too many tries. Wait a few minutes.");
  }
  if (/unavailable|network/.test(code)) {
    return new AuthError("network", "Couldn't reach the server. Check your connection and try again.");
  }
  return new AuthError("unknown", message.replace(/^.*?:\s*/, "") || "That didn't work. Try again.");
}

/**
 * Dev-only emulator-gated sign-in. Throws unless
 * `VITE_FIREBASE_USE_EMULATORS=true`. See `signInWithTestUser` in
 * `./authClient` for behavior + caveats.
 */
export async function signInWithTestUser(
  email: string,
  password?: string
): Promise<AuthUser> {
  markSignInStarted("account");
  try {
    return await signInWithTestUserClient(email, password);
  } catch (err) {
    reportSignInFailure("account", err);
    throw err;
  }
}

function reportSignInFailure(surface: SignInSurface, err: unknown): void {
  const kind = err instanceof AuthError ? err.kind : "unknown";
  // The page is leaving for Google; the sign-in resumes on return.
  if (kind === "redirecting") return;
  markSignInFailed(surface, kind);
}

/**
 * Sign the user out and scrub every trace of them from this device:
 *
 *   1. Exit any active shared-room session **while still authenticated**
 *      so Firestore/RTDB teardowns succeed with the user's credentials.
 *   2. `await` the Firebase sign-out. The `onAuthStateChanged` listeners
 *      run synchronously inside this call, which is what causes the
 *      `librarySync` / `productsSync` / `sharedRoomsRegistry`
 *      orchestrators to unsubscribe from the local Zustand stores.
 *   3. With those subscribers gone, it's now safe to reset the local
 *      stores and wipe app-owned `localStorage` keys without echoing
 *      destructive writes back up to the user's cloud data.
 *
 * The ordering matters — see {@link clearLocalAppData} for why doing
 * the wipe before step 2 would silently delete the user's Firestore
 * rooms/products.
 */
export async function signOut(): Promise<void> {
  exitSharedRoom();
  await signOutClient();
  clearLocalAppData();
}

/**
 * True when the multiplayer testing harness is active. Components can
 * use this to render emulator-only UI (e.g. test-user sign-in buttons).
 * Vite inlines `import.meta.env` at build time, so production bundles
 * tree-shake any branch gated on this flag.
 */
export function isEmulatorMode(): boolean {
  return (
    (import.meta.env.VITE_FIREBASE_USE_EMULATORS as string | undefined) ===
    "true"
  );
}

export { isAuthAvailable, AuthError, waitForAuthReady } from "./authClient";
export type { SignInSurface } from "./events";
export type { AuthUser } from "./authClient";

/** Test-only: tear down the singleton subscription so tests start clean. */
export function _resetAuthForTests(): void {
  if (unsubFromFirebase) unsubFromFirebase();
  unsubFromFirebase = null;
  subscribed = false;
  listeners.clear();
  state = {
    status: isAuthAvailable() ? "loading" : "unavailable",
    user: null,
  };
}
